// Sharing the card: the Web Share API with the image file where the phone supports it; otherwise the page shows the image
// to press and hold, and a copy-link button.
import { placeText } from './card';

export type ShareOutcome = 'shared' | 'cancelled' | 'unsupported' | 'failed';
type ShareNav = Pick<Navigator, 'share' | 'canShare'> & { clipboard?: Pick<Clipboard, 'writeText'> };

export function canShareFile(file: File, nav: Partial<ShareNav> = navigator): boolean {
  try { return typeof nav.share === 'function' && typeof nav.canShare === 'function' && nav.canShare({ files: [file] }); } catch { return false; }
}

export async function shareFile(file: File, text: string, nav: Partial<ShareNav> = navigator): Promise<ShareOutcome> {
  if (!canShareFile(file, nav)) return 'unsupported';
  try { await nav.share!({ files: [file], title: 'MedSnapp Rounds', text }); return 'shared'; }
  catch (e) { return (e as { name?: string })?.name === 'AbortError' ? 'cancelled' : 'failed'; }
}

export async function copyText(text: string, nav: Partial<ShareNav> = navigator): Promise<boolean> {
  try { await nav.clipboard!.writeText(text); return true; } catch { return false; }
}

/** The words that travel with the card: the place as people say it, and the brand once. */
export function shareText(p: { rank?: number; players: number; title: string; url: string }): string {
  const where = p.title.trim() ? ` in ${p.title.trim()}` : '';
  return p.rank
    ? `I finished ${placeText(p.rank, p.players)}${where} on MedSnapp Rounds. Your turn: ${p.url}`
    : `I played${where} on MedSnapp Rounds. Your turn: ${p.url}`;
}

// ---------------------------------------------------------------------------------------------------------------------
// 14 Oct: the share chain (api/src/core/sharing.ts). A player's invite link carries their own tag, made here exactly as
// the server makes it, so the copy can happen inside the tap; POST /me/shared then counts the tap (fire and forget).

/** `s-<CODE>-<first 8 hex of the player id>`, as core/sharing shareTag. */
export function inviteTag(code: string, playerId: string): string {
  return `s-${code.toUpperCase()}-${playerId.replace(/-/g, '').slice(0, 8).toLowerCase()}`;
}
/** The player's own invite link: the open battles, tagged. */
export function inviteUrl(code: string, playerId: string, origin: string = window.location.origin): string {
  return `${origin}/open?ref=${inviteTag(code, playerId)}`;
}
/** Counts a share or copy tap on the server; never waits and never fails the share. */
export function countShare(token: string | undefined, url: (path: string) => string): void {
  if (!token) return;
  try { void fetch(url('/me/shared'), { method: 'POST', keepalive: true, headers: { authorization: `Bearer ${token}` } }).catch(() => {}); } catch { /* the share went out anyway */ }
}
